import { CommonModule } from '@angular/common'
import { AfterViewInit, Component, DestroyRef, inject, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { Router, RouterModule } from '@angular/router'
import {
  ButtonComponent,
  IllustrationComponent,
  ModalComponent,
  ModalService,
  ModalSize,
  unwrapData,
} from '@ftr/foundation'
import { AppPaths } from '@ftr/routing-paths'
import { Store } from '@ngxs/store'
import { combineLatest, filter, map, Observable, switchMap } from 'rxjs'
import { UserSettingsService } from '../../services'
import {
  GetCurrentUserSettingsAction,
  MfaSetupModalAction,
  MfaSetupModalType,
  SetMfaSetupModal,
  UserState,
} from '../../store'

@Component({
  selector: 'ftr-mfa-setup-modal',
  templateUrl: './mfa-setup-modal.component.html',
  styleUrls: ['./mfa-setup-modal.component.css'],
  imports: [ModalComponent, IllustrationComponent, ButtonComponent, RouterModule, CommonModule],
})
export class MfaSetupModalComponent implements AfterViewInit {
  @ViewChild('mfaSetupModalTemplate') mfaSetupModalTemplate: TemplateRef<any>

  mfaSetupModalType: MfaSetupModalType
  mfaSetupModalAction: MfaSetupModalAction

  readonly modalSize = ModalSize
  readonly mfaSetupModalTypes = MfaSetupModalType
  readonly mfaSetupModalActions = MfaSetupModalAction
  readonly mfaSetupPath = `${AppPaths.Account}/${AppPaths.MultiFactorAuthentication}`

  readonly #destroyRef = inject(DestroyRef)

  constructor(
    private readonly modalService: ModalService,
    private readonly store: Store,
    private readonly router: Router,
    private readonly modalContainer: ViewContainerRef,
    private readonly userSettingsService: UserSettingsService,
  ) {}

  ngAfterViewInit(): void {
    this.store
      .select(UserState.isLoggedIn)
      .pipe(
        filter(isLoggedIn => isLoggedIn),
        switchMap(_ => this.store.dispatch(new GetCurrentUserSettingsAction())),
        switchMap(_ => {
          return combineLatest([this.getUserMfaModalType(), this.store.select(UserState.mfaSetupModalAction)])
        }),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(([modalType, modalAction]) => {
        this.mfaSetupModalType = modalType
        this.mfaSetupModalAction = modalAction

        if (modalType !== MfaSetupModalType.Closed) {
          this.modalService.open(this.mfaSetupModalTemplate, this.modalContainer)
        }
      })
  }

  closeModal(): void {
    this.store.dispatch(new SetMfaSetupModal(MfaSetupModalType.Closed))
    this.modalService.close()
  }

  notNow(): void {
    if (this.mfaSetupModalType !== MfaSetupModalType.Generic) {
      this.closeModal()
      return
    }
    this.userSettingsService
      .updateUserSettings({ showMfaSetup: false })
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(_ => this.closeModal())
  }

  navigateToSetupMfa(): void {
    this.closeModal()
    this.router.navigate([this.mfaSetupPath])
  }

  signOut(): void {
    this.closeModal()
    this.router.navigate([AppPaths.Logout])
  }

  private getUserMfaModalType(): Observable<MfaSetupModalType> {
    return combineLatest([
      this.store.select(UserState.currentUserEmail).pipe(unwrapData()),
      this.store.select(UserState.currentUserSettings),
      this.store.select(UserState.mfaSetupModalType),
    ]).pipe(
      map(([userEmail, userSettings, modalType]) => {
        if (isE2EUser(userEmail)) {
          return MfaSetupModalType.Closed
        }

        if (!userSettings.showMfaSetup && modalType === MfaSetupModalType.Generic) {
          return MfaSetupModalType.Closed
        }

        return modalType
      }),
    )
  }
}

function isE2EUser(currentUserEmail: string): boolean {
  return currentUserEmail.endsWith('@mailosaur.io')
}
